import { useEffect, useMemo, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import { useAdminCoverage, type CoverageChurch } from '../../hooks/useAdmin'

import { MAPBOX_TOKEN as TOKEN } from '../../lib/geocode'

const STATUS_COLORS: Record<CoverageChurch['status'], string> = {
  claimed:    '#4F5E32',
  active:     '#22c55e',
  stale:      '#f59e0b',
  discovered: '#94a3b8',
  bare:       '#cbd5e1',
}

const STATUS_LABELS: Record<CoverageChurch['status'], string> = {
  claimed:    'Claimed',
  active:     'Active (has upcoming events)',
  stale:      'Stale (scraped, no upcoming events)',
  discovered: 'Discovered (has website, not scraped)',
  bare:       'Bare (no website found)',
}

type StatusKey = CoverageChurch['status']
const ALL_STATUSES: StatusKey[] = ['claimed', 'active', 'stale', 'discovered', 'bare']

export default function AdminCoverageMapPage() {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markersRef = useRef<mapboxgl.Marker[]>([])

  const { data, isLoading } = useAdminCoverage()
  const [visibleStatuses, setVisibleStatuses] = useState<Set<StatusKey>>(new Set(ALL_STATUSES))

  const churches = useMemo(() => data?.data ?? [], [data])

  const counts = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = churches.filter((c) => c.status === s).length
    return acc
  }, {} as Record<StatusKey, number>)

  useEffect(() => {
    if (!mapContainerRef.current || !TOKEN) return
    let cancelled = false

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !mapContainerRef.current) return
      const mb = mod.default
      mb.accessToken = TOKEN

      const map = new mb.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-98.5795, 39.8283],
        zoom: 4,
        attributionControl: false,
      })

      map.on('load', () => {
        if (cancelled) return
        mapRef.current = map
        map.resize()
      })
    })

    return () => {
      cancelled = true
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.loaded()) return

    void import('mapbox-gl').then((mod) => {
      const mb = mod.default
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []

      for (const church of churches) {
        if (!visibleStatuses.has(church.status)) continue

        const el = document.createElement('div')
        el.style.cssText = `
          width: 10px; height: 10px; border-radius: 50%;
          background: ${STATUS_COLORS[church.status]};
          border: 1.5px solid rgba(255,255,255,0.8);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        `
        el.title = church.name

        const popup = new mb.Popup({ offset: 10, closeButton: false, maxWidth: '220px' })
          .setHTML(`
            <div style="font-family:sans-serif;font-size:12px;line-height:1.5">
              <div style="font-weight:600;font-size:13px;margin-bottom:2px">${church.name}</div>
              <div style="color:#64748b;text-transform:capitalize">${STATUS_LABELS[church.status]}</div>
            </div>
          `)

        const marker = new mb.Marker({ element: el })
          .setLngLat([church.lng, church.lat])
          .setPopup(popup)
          .addTo(map)

        markersRef.current.push(marker)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churches, visibleStatuses])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const onLoad = () => setVisibleStatuses((prev) => new Set(prev))
    map.on('load', onLoad)
    return () => { map.off('load', onLoad) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function toggleStatus(s: StatusKey) {
    setVisibleStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(s)) { next.delete(s) } else { next.add(s) }
      return next
    })
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{ width: 260, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid var(--line-soft)', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <div className="eyebrow mb-1">Coverage</div>
          <h2 className="serif m-0 text-[22px] tracking-[-0.01em]">Church map</h2>
          {isLoading && <div className="text-[12px] text-muted mt-1">Loading…</div>}
        </div>

        {!isLoading && (
          <div className="flex flex-col gap-1.5">
            {ALL_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border-0 cursor-pointer font-sans text-left w-full"
                style={{ background: visibleStatuses.has(s) ? 'var(--surface-2)' : 'transparent', opacity: visibleStatuses.has(s) ? 1 : 0.45 }}
              >
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[s], display: 'inline-block', flexShrink: 0 }} />
                <span className="text-[12.5px] text-ink-2 flex-1">{STATUS_LABELS[s].split(' (')[0]}</span>
                <span className="text-[12px] font-semibold text-ink mono">{counts[s]}</span>
              </button>
            ))}
            <div className="text-[11px] text-muted mt-1 pl-1">{churches.length} total · click to toggle</div>
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        {!TOKEN && (
          <div className="absolute inset-0 flex items-center justify-center text-[13px] text-muted">
            Mapbox token not configured
          </div>
        )}
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>
  )
}
